import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { 
  Send,
  CheckCircle,
  ArrowLeft,
  ChevronDown,
  Mail
} from 'lucide-react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import chronoLogo from '../images/Chrono Session - removed bg logo.png'

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    inquiryType: 'general',
    message: '',
    priority: 'medium'
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'support', label: 'Technical Support' },
    { value: 'sales', label: 'Sales & Pricing' },
    { value: 'premium', label: 'Premium Plan' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'feedback', label: 'Feedback & Suggestions' },
    { value: 'media', label: 'Media & Press' },
    { value: 'other', label: 'Other' }
  ]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    try {
      const form = new FormData()
      form.append('access_key', 'a4cea107-46cc-43b2-93fe-2be7f430f12a')
      form.append('name', formData.name)
      form.append('email', formData.email)
      form.append('phone', formData.phone)
      form.append('subject', formData.subject)
      form.append('inquiry_type', formData.inquiryType)
      form.append('priority', formData.priority)
      form.append('message', formData.message)
      form.append('from_name', 'Chrono Session Contact Form')
      
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: form
      })
      
      if (response.ok) {
        setShowSuccessModal(true)
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          inquiryType: 'general',
          message: '',
          priority: 'medium'
        })
      } else {
        throw new Error('Form submission failed')
      }
    } catch (error) {
      console.error('Contact form error:', error)
      alert('Failed to send message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#f3f4f6] flex flex-col font-sans text-gray-900">
      <Helmet>
        <title>Contact Us - Chrono Session</title>
        <meta name="description" content="Get in touch with the Chrono Session support team." />
      </Helmet>

      {/* Professional Header - Clean & Minimal */}
      <nav className="bg-white border-b border-gray-300 sticky top-0 z-40 h-16">
        <div className="max-w-6xl mx-auto px-4 h-full flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            <div className="h-4 w-px bg-gray-300" />
            <div className="flex items-center gap-2">
              <LazyLoadImage 
                src={chronoLogo} 
                alt="Chrono Session" 
                className="h-6 w-auto" 
                effect="blur"
              />
              <span className="font-semibold text-gray-800 tracking-tight">Support</span>
            </div>
          </div>
          
          <div className="hidden md:flex items-center text-sm font-medium text-gray-600">
            <a href="mailto:chronosession.app@gmail.com" className="hover:text-blue-700 transition-colors flex items-center gap-2">
              <Mail className="w-4 h-4" />
              chronosession.app@gmail.com
            </a>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-start justify-center pt-8 pb-12 px-4">
        <motion.div 
          className="w-full max-w-[640px] bg-white rounded-md shadow-sm border border-gray-200"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="p-8">
            <header className="mb-8 border-b border-gray-100 pb-4">
              <h1 className="text-2xl font-bold text-gray-900">Submit a request</h1>
              <p className="text-sm text-gray-500 mt-1">
                Our team is happy to help. We usually respond within a few hours.
              </p>
            </header>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-1.5">
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 rounded border border-gray-300 text-gray-900 text-sm focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-colors"
                    placeholder="e.g. John Doe"
                  />
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 rounded border border-gray-300 text-gray-900 text-sm focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-colors"
                    placeholder="name@company.com"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {/* Phone */}
                <div className="space-y-1.5">
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 rounded border border-gray-300 text-gray-900 text-sm focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-colors"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                {/* Inquiry Type */}
                <div className="space-y-1.5">
                  <label htmlFor="inquiryType" className="block text-sm font-semibold text-gray-700">
                    Inquiry Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      required
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 rounded border border-gray-300 text-gray-900 text-sm focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-colors appearance-none bg-white cursor-pointer"
                    >
                      {inquiryTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-1.5">
                <label htmlFor="subject" className="block text-sm font-semibold text-gray-700">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  id="subject"
                  required
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 rounded border border-gray-300 text-gray-900 text-sm focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-colors"
                />
              </div>

              {/* Message */}
              <div className="space-y-1.5">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 rounded border border-gray-300 text-gray-900 text-sm focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-colors resize-y min-h-[100px]"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2.5 bg-[#0F62FE] hover:bg-blue-700 text-white text-sm font-medium rounded-sm shadow-sm transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <span>Submit Request</span>
                      <Send className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
          
          <div className="bg-[#f9fafb] border-t border-gray-200 px-8 py-4">
             <div className="flex items-center justify-between text-xs text-gray-500">
                <p>Protected by reCAPTCHA</p>
                <a href="/#faq" className="text-blue-600 hover:underline">View FAQ</a>
             </div>
          </div>
        </motion.div>
      </div>

      {/* Professional Success Modal - Crisp & Clean */}
      <AnimatePresence>
        {showSuccessModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-md shadow-2xl w-full max-w-sm overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <CheckCircle className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Request Submitted</h3>
                    <p className="text-xs text-gray-500">Ticket #29384 created</p>
                  </div>
                </div>
                
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                   Thanks for reaching out! We've received your request and our support team will get back to you at <strong>{formData.email}</strong> shortly.
                </p>
                
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-900 text-sm font-medium rounded border border-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ContactPage
